import merge from 'lodash.merge'
import cloneDeep from 'lodash.clonedeep'
import { translateToItem } from './orderItemFunctions'
import {
  orderItemBase,
  orderItemModifierBase
} from './orderBase.js'
import { OrderItemStates, OrderStates, OrderStatus } from '../consts'
import ordereduuid from '../Helpers/timeuuid'

export default function itemsProcessor(order, offlineEnabled, clientSocket, catalog) {
  function insertUpdateItem(updatedItem, orderObj = null) {
    if (updatedItem == null) return null
    let idx = getItemIndex(updatedItem.orderItemUUID, orderObj)
    if(idx == null) {
      if (orderObj) {
        orderObj.items.push(updatedItem)
      } else {
        order.order.items.push(updatedItem)
      }
      return updatedItem.orderItemUUID
    } else {
      if (orderObj) {
        delete orderObj.items[idx].modifiers
        merge(orderObj.items[idx], updatedItem)
        return updatedItem.orderItemUUID
      } else {
        delete order.order.items[idx].modifiers
        merge(order.order.items[idx], updatedItem)
        return updatedItem.orderItemUUID
      }
    }    
  }

  function getItemIndex(orderItemUUID = '', orderObj = null) {
    if (!orderObj) {
      let idx = 0
      for (let item of order.order.items || []) {
        let match = orderItemUUID !== '' ? item.orderItemUUID === orderItemUUID : false
        if (match) return idx // Found
        idx++
      }
      //if (itemCache == null) reCacheItems()
      //return itemCache[orderItemUUID]
    } else {
      let idx = 0
      for (let item of orderObj.items || []) {
        let match = orderItemUUID !== '' ? item.orderItemUUID === orderItemUUID : false
        if (match) return idx // Found
        idx++
      }
    }
    return null
  }

  function getItem(orderItemUUID = '', orderObj = null) {
    let itemIndex = getItemIndex(orderItemUUID, orderObj)
    if(itemIndex == null) return null
    if (!orderObj) {
      return order.order.items[itemIndex]
    } else {
      return orderObj.items[itemIndex]
    }
  }

  // Strips the bloated order item modifier from all the unnecessary fields
  function cleanOrderItemModifier(modifier) {
    let _modifier = merge(
      cloneDeep(orderItemModifierBase),
      cloneDeep(modifier)
    )
    for (let key in _modifier) {
      if (!(key in orderItemModifierBase)) {
        //console.log('DEL MODIFIER KEY:', key)
        delete _modifier[key]
      }
    }
    return _modifier
  }

  // Strips the bloated order item from all the unnecessary fields
  function cleanOrderItem(item) {
    let _item = merge(cloneDeep(orderItemBase), cloneDeep(item))
    for (let key in _item) {
      if (!(key in orderItemBase)) {
        //console.log('DEL ITEM KEY:', key)
        delete _item[key]
      }
    }
    for (let modifier of _item.modifiers) {
      modifier = cleanOrderItemModifier(modifier)
    }
    return _item
  }

  function getItemsByUUID(itemUUID, categoryUUID = null) {
    let items = []
    for (let orderItem of order.order.items) {
      if (
        orderItem.itemUUID === itemUUID &&
        (orderItem.categoryUUID === categoryUUID || categoryUUID == null)
      ) {
        let returnItem = order.assembledOrderItems[orderItem.orderItemUUID]
        returnItem != null && items.push(returnItem)
      }
    }
    if (items.length === 0) { items.push(catalog.assembleItem(itemUUID, true, order.order.locationUUID, null, order.order)) }
    return items
  }

  function getItemsByParent(orderItemUUID) {
    let items = []
    for (let orderItem of order.order.items) {
      if (
        orderItem.parentOrderItemUUID === orderItemUUID
      ) {
        items.push(orderItem)
      }
    }
    return items
  }

  function getState(lastStatus) {
    switch (lastStatus?.statusID) {
      case OrderStatus.ORDER_ITEM_DONE: {
        return { state: OrderItemStates.DONE }
      }
      case OrderStatus.ORDER_ITEM_PREPARING: {
        return { state: OrderItemStates.PREPARING }
      }
      case OrderStatus.ORDER_ITEM_RECEIVEDBYCOSTUMER: {
        return { state: OrderItemStates.ATCUSTOMER }
      }
      case OrderStatus.ORDER_ITEM_STORNO: {
        return { state: OrderItemStates.STORNO, stateReason: lastStatus?.notes?.reasonCode || 0 }
      }
      default: {
        return { state: OrderItemStates.WAITING }
      }
    }
  }

  function determineOrderItemState(orderItemUUID) {
    if (orderItemUUID == null) return 0
    let statuses = order.statuses.getItemStatuses(orderItemUUID)
    // Check last status
    let lastStatus = statuses?.[statuses?.length - 1] || null
    let state = getState(lastStatus)
    if(order.order.orderState >= OrderStates.PREPARING && state.state < order.order.orderState) {
      state.state = order.order.orderState
    }
    return state
  }

  function determineOrderItemPayment(orderItemUUID) {
    if (orderItemUUID == null) return null
    let payment = order.externalPayments.calculateItemTotal(orderItemUUID)
    if(payment.amount === 0) {
      if(order.order?.paymentsTotal > 0 && order.order.paymentState === 1) {
        let item = order.assembledOrderItems[orderItemUUID]
        payment.amount = item?.discountPrice || 0
        payment.count = item?.count || 0
      }
    }
    return payment
  }

  function assembleItem(itemUUID, orderItemUUID) {
    if (catalog == null) return null
    try {
      let orderItem = orderItemUUID != null ? getItem(orderItemUUID) : null
      if (orderItem != null) {
        let item = catalog.assembleItem(
          orderItem.itemUUID,
          false,
          order.order.locationUUID,
          orderItem.selectedSizeUUID,
          order.order
        )
        if (item == null) return null
        item = translateToItem(item, orderItem)
        item.payment = determineOrderItemPayment(orderItemUUID)
        let state = determineOrderItemState(orderItemUUID, item)
        item.state = state.state
        item.stateReason = state.stateReason
        item.count = (item.state === OrderItemStates.STORNO ? 0 : item.count)
        catalog.checkItemValidity(item)
        catalog.calculateItemPrices(item, order.order)
        return item
      } else {
        if (itemUUID == null) return null
        let item = catalog.assembleItem(
          itemUUID,
          true,
          order.order.locationUUID,
          null,
          order.order
        )
        catalog.calculateItemPrices(item, order.order)
        return item
      }
    } catch (e) {
      console.error(e)
      return null
    }
  }

  function getCount() {
    let count = 0
    for (let orderItem of Object.values(order.assembledOrderItems)) {
      count += (orderItem.count != null && orderItem.state <=OrderItemStates.STORNO) ? orderItem.count : 0
    }
    return count
  }

  // Returns a new item with the specified count, reducing the original item count
  function sliceItem(orderItemUUID, count) {
    let orderItem = getItem(orderItemUUID)
    if (orderItem == null) return null
    if(count === 0 || count >= orderItem.count) return null
    let slicedItem = cloneDeep(orderItem)
    slicedItem.count = count
    slicedItem.orderItemUUID = ordereduuid()
    orderItem.count -= count
    insertUpdateItem(orderItem)
    insertUpdateItem(slicedItem)
    order.itemDiscounts.updateItemDiscount(orderItemUUID, true, true)
    return slicedItem
  }

  return {
    insertUpdateItem,
    getItem,
    cleanOrderItem,
    getItemsByUUID,
    assembleItem,
    getCount,
    sliceItem,
    getItemsByParent
  }
}
